/**
 * The password gate.
 *
 * There is one password and no username, so there is nothing to enumerate and
 * nothing useful to say about a failure beyond that it failed. The server rate
 * limits this route hard; the only job here is to not get in the way.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { meKey } from '../App'
import { api, ApiError } from '../lib/api'

export function LoginPage() {
  const [password, setPassword] = useState('')
  const queryClient = useQueryClient()

  const login = useMutation({
    mutationFn: (value: string) => api.post<{ ok: true }>('/login', { password: value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
  })

  const tooMany = login.error instanceof ApiError && login.error.status === 429

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <form
        className="panel w-full max-w-sm p-6"
        onSubmit={(e) => {
          e.preventDefault()
          if (password) login.mutate(password)
        }}
      >
        <div className="flex items-center gap-2.5">
          <img src="/brand/exlyr-mark-256.png" alt="" width={26} height={26} aria-hidden />
          <span className="text-base font-bold tracking-tight">Elyxr admin</span>
        </div>

        <p className="mt-3 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
          This console can move real balances. Do not open it on a shared machine, and sign out
          when you are done — the session lasts eight hours.
        </p>

        <label htmlFor="password" className="eyebrow mt-6 block">
          Password
        </label>
        <input
          id="password"
          className="input mt-1.5"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            login.reset()
          }}
        />

        <button
          className="btn btn-primary mt-4 w-full"
          type="submit"
          disabled={!password || login.isPending}
        >
          {login.isPending ? 'Checking…' : 'Sign in'}
        </button>

        {login.error && (
          <p className="mt-3 text-xs" role="alert" style={{ color: 'var(--color-down)' }}>
            {tooMany
              ? 'Too many attempts. Wait a few minutes before trying again.'
              : login.error instanceof ApiError
                ? login.error.message
                : 'Could not sign in.'}
          </p>
        )}
      </form>
    </div>
  )
}
